import { useCallback, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { PortalProps } from '@frontend/components';

import type { OnReorderParams } from './types';
import { SortableListContextProvider } from './utils';

type SortableListProviderProps = PropsWithChildren<{
  items: unknown[];
  onReorderStart?: ({ index }: { index: number }) => void;
  onReorderFinished: ({ oldIndex, newIndex }: OnReorderParams) => void;
  /** The container around the sortable list. Useful when you need to render something outside of the list. */
  containerRef?: PortalProps['containerRef'];
}>;

export const SortableListProvider = ({
  items,
  onReorderFinished,
  onReorderStart,
  children,
  containerRef,
}: SortableListProviderProps) => {
  const [orderedItems, setOrderedItems] = useState([...items]);

  const [prevItems, setPrevItems] = useState(items);
  if (items !== prevItems) {
    setPrevItems(items);
    setOrderedItems([...items]);
  }

  const onLocalReorder = useCallback(
    ({ oldIndex, newIndex }: OnReorderParams) => {
      setOrderedItems((prevOrderedItems) => {
        const nextOrderedItems = [...prevOrderedItems];
        const [itemToReorder] = nextOrderedItems.splice(oldIndex, 1);
        nextOrderedItems.splice(newIndex, 0, itemToReorder);
        return nextOrderedItems;
      });
    },
    [],
  );

  return (
    <SortableListContextProvider
      value={{
        orderedItems,
        onLocalReorder,
        onReorderFinished,
        onReorderStart,
        containerRef,
      }}
    >
      {children}
    </SortableListContextProvider>
  );
};
